import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberDeclarations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // quota_id being downloaded

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/members/quota/history`, { withCredentials: true });
        setItems(r.data.items || []);
      } catch { toast.error("Erro a carregar as declarações."); } finally { setLoading(false); }
    })();
  }, []);

  const download = async (q) => {
    setDownloading(q.id);
    try {
      const r = await axios.get(`${API}/members/quota/${q.id}/receipt.pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `declaracao-quota-${q.year}-${String(q.receipt_number || 0).padStart(4, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Não foi possível obter o PDF.");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-declarations-loading">
        <FileText className="h-6 w-6 text-[hsl(var(--accent))]" />
        <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">A carregar histórico…</p>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-declarations-empty">
        <FileText className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
        <h3 className="mt-5 display-heading text-2xl">Declarações</h3>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Ainda não tem declarações de quota emitidas. Aparecerão aqui após o primeiro pagamento.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-declarations-list">
      <div className="flex items-center gap-3">
        <FileText className="h-6 w-6 text-[hsl(var(--accent))]" />
        <h3 className="display-heading text-2xl">Histórico de Declarações</h3>
      </div>
      <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
        Descarregue os PDFs das suas Declarações de Donativo para entregar à contabilidade.
      </p>

      <ul className="mt-6 divide-y divide-[hsl(var(--border))]">
        {items.map((q) => {
          const issuedTo = q.billing?.issue_to === "company"
            ? (q.billing?.company || "Empresa")
            : (q.billing?.name || "Pessoa singular");
          return (
            <li key={q.id} className="py-4 flex items-center justify-between gap-4" data-testid={`declaration-row-${q.year}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[hsl(var(--foreground))]">
                  Quota {q.year}
                  {q.receipt_number ? (
                    <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                      · Nº {String(q.receipt_number).padStart(4, "0")}
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                  {q.tier_label || "—"} · {Number(q.amount_eur || 0).toFixed(2)} € · em nome de <span className="text-[hsl(var(--foreground))]/80">{issuedTo}</span>
                </div>
              </div>
              <button
                onClick={() => download(q)}
                disabled={downloading === q.id}
                className="inline-flex items-center gap-2 px-3 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-[11px] uppercase tracking-[0.18em] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors disabled:opacity-50 shrink-0"
                data-testid={`download-declaration-${q.year}`}
                title="Descarregar PDF"
              >
                {downloading === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                PDF
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
